// Switches the app between light and dark by flipping the `data-theme` attribute
// on <html> and persisting the choice in localStorage. The attribute is applied
// before first paint by an inline script in root.html.heex to avoid a flash.
//
// There are three choices but only two themes: "light" and "dark" pin the theme,
// "system" follows the OS and keeps following it while the page is open. So the
// stored value is the user's *choice*, and the attribute is the *resolved*
// theme — those are not the same thing, and conflating them is what makes a
// system option look broken when the OS flips.
//
// Without `data-set-theme` the element toggles between light and dark, which is
// what a single button wants. With it, the element selects that choice outright,
// and every such element in the document gets its `aria-pressed` kept in sync,
// wherever it happens to live.
const STORAGE_KEY = "theme";
const CHOICES = ["light", "dark", "system"];

const prefersDark = () => window.matchMedia("(prefers-color-scheme: dark)");

function storedChoice() {
  let value;
  try {
    value = localStorage.getItem(STORAGE_KEY);
  } catch {
    value = null;
  }

  // Absent still means light. Treating it as "system" would move everyone who
  // has never touched the toggle onto their OS setting without being asked.
  return CHOICES.includes(value) ? value : "light";
}

function resolve(choice) {
  return choice === "system" ? (prefersDark().matches ? "dark" : "light") : choice;
}

function syncPressedState(choice) {
  for (const el of document.querySelectorAll("[data-set-theme]")) {
    el.setAttribute("aria-pressed", String(el.dataset.setTheme === choice));
  }
}

function applyResolved(theme) {
  if (theme === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

function applyChoice(choice) {
  applyResolved(resolve(choice));

  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // localStorage may be unavailable (e.g. private mode); ignore.
  }

  syncPressedState(choice);
}

// One listener for the document rather than one per button: the OS can change
// while the menu is closed, and the hook that happens to be mounted should not
// decide whether we hear about it.
let watching = false;

function watchSystem() {
  if (watching) return;
  watching = true;

  prefersDark().addEventListener("change", () => {
    if (storedChoice() === "system") applyResolved(resolve("system"));
  });
}

export const ThemeToggle = {
  mounted() {
    watchSystem();

    this.handleClick = () => {
      const choice =
        this.el.dataset.setTheme || (resolve(storedChoice()) === "dark" ? "light" : "dark");

      applyChoice(choice);
    };

    this.el.addEventListener("click", this.handleClick);

    // The server cannot know the stored choice, so its guess at `aria-pressed`
    // is corrected here once the real one is known.
    if (this.el.dataset.setTheme) {
      syncPressedState(storedChoice());
    }
  },

  destroyed() {
    this.el.removeEventListener("click", this.handleClick);
  },
};
